import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, User, Shield, LogOut, AlertCircle, Loader } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useAuth } from '../auth/AuthContext';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const { isDark } = useTheme();
  const location = useLocation();
  const { 
    isAuthenticated, 
    isAdmin, 
    user, 
    loading, 
    error, 
    inProgress,
    logout, 
    clearError 
  } = useAuth();

  const handleAuthAction = async () => {
    if (error) {
      clearError();
    }
    
    // Prevent multiple interactions (allow Logout when SelectingVenue so user can escape)
    if (inProgress !== 'none' && inProgress !== undefined && inProgress !== null && inProgress !== 'SelectingVenue') {
      console.log('⚠️ Authentication interaction already in progress');
      return;
    }
    
    if (isAuthenticated) {
      await logout();
    } else {
      // Use the primary login page; admin role is determined by the normal flow
      window.location.href = '/login';
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.name || user.displayName || user.mail || user.userPrincipalName || 'User';
  };

  // Exclude SelectingVenue so Logout remains clickable when user is stuck in venue modal
  const isAuthInProgress = ((inProgress && inProgress !== 'none' && inProgress !== 'SelectingVenue') || loading);
  const isSelectingVenue = inProgress === 'SelectingVenue';

  const getAuthButtonText = () => {
  if (inProgress === 'Login') return 'Signing In...';
  if (inProgress === 'Logout') return 'Signing Out...';
  if (inProgress === 'HandleRedirect') return 'Processing...';
    if (loading) return 'Loading...';
    if (isAuthenticated) return 'Logout';
    return 'Login';
  };

  const getAuthButtonIcon = () => {
  if (isAuthInProgress) {
      return <Loader className="w-3 h-3 animate-spin" />;
    }
    if (isAuthenticated) {
      return <LogOut className="w-3 h-3" />;
    }
    return <User className="w-3 h-3" />;
  };

  return (
    <nav className="relative z-[60] bg-white dark:bg-gray-800 shadow-soft border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          
          {/* Left Side - COMPLETELY EMPTY */}
          <div className="w-16">
            {/* NO CONTENT - No CN logo, no "Order System" text, nothing */}
          </div>

          {/* Center Navigation */}
          <div className="flex items-center justify-center flex-1 space-x-1 flex-wrap">
            {isAuthenticated && (
              <IoNavLinks
                isAdmin={isAdmin}
                user={user}
                location={location}
              />
            )}
          </div>

          {/* Right Side - User Info, Auth Button, and Theme Toggle */}
          <div className="flex items-center space-x-3 w-auto">
            
            {/* User Info - Only show when fully authenticated */}
            {isAuthenticated && !isAuthInProgress && (
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">
                  {getUserDisplayName()}
                </span>
                {isAdmin && (
                  <Shield className="w-4 h-4 text-green-500" title="Admin User" />
                )}
              </div>
            )}
            
            {/* Error Display */}
            {error && (
              <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 text-xs">
                <AlertCircle className="w-4 h-4" />
                <span className="hidden sm:inline max-w-32 truncate" title={error}>
                  {error}
                </span>
              </div>
            )}

            {/* Authentication Button */}
            <button
              onClick={handleAuthAction}
              disabled={isAuthInProgress}
              className={`
                flex items-center space-x-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 min-w-[100px] justify-center
                ${isAuthenticated && !isAuthInProgress
                  ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800' 
                  : isAuthInProgress
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 cursor-wait'
                  : 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                }
                ${isAuthInProgress ? 'animate-pulse' : ''}
                ${error ? 'ring-2 ring-red-300 dark:ring-red-700' : ''}
              `}
              title={
                isAuthenticated 
                  ? "Logout" 
                  : isAuthInProgress 
                  ? "Authentication in progress..." 
                  : "Login with Microsoft Azure"
              }
            >
              {getAuthButtonIcon()}
              <span className="hidden sm:inline">
                {getAuthButtonText()}
              </span>
            </button>

            {/* Theme Toggle */}
            <ThemeToggle />
          </div>
        </div>
      </div>
      
      {/* Progress bar for authentication processes */}
      {isAuthInProgress && (
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700">
          <div className="h-full bg-blue-600 animate-pulse" style={{ width: '70%' }}></div>
        </div>
      )}
    </nav>
  );
};

function navCls(active) {
  return `flex items-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors ${
    active
      ? 'bg-primary-100 dark:bg-blue-900 text-primary-700 dark:text-blue-300'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-white'
  }`
}

function IoNavLinks({ isAdmin, user, location }) {
  const path = location.pathname
  const type = user?.userType
  const venue = type === 'venue'
  const affiliate = type === 'affiliate'
  const sales = !isAdmin && !venue && !affiliate

  const Item = ({ to, match, children }) => (
    <Link to={to} className={navCls(typeof match === 'function' ? match(path) : path === to || (match && path.startsWith(match)))}>
      <span className="font-medium">{children}</span>
    </Link>
  )

  if (venue) {
    return (
      <>
        <Item to="/" match={(p) => p === '/' || p === '/app' || p.startsWith('/campaigns/venue/new')}>Campaigns</Item>
        <Item to="/inventory">My screens</Item>
        <Item to="/campaigns?kind=house_message" match={(p) => p.startsWith('/campaigns')}>History</Item>
      </>
    )
  }

  if (affiliate) {
    return (
      <>
        <Item to="/campaigns" match={(p) => p === '/campaigns' || p === '/campaigns/new' || p.startsWith('/campaigns/')}>Campaigns</Item>
        <Item to="/audience" match="/audience">Audience</Item>
        <Item to="/inventory">Inventory</Item>
        <Item to="/admin">History</Item>
      </>
    )
  }

  if (sales) {
    return (
      <>
        <Item to="/rfp" match="/rfp">RFP</Item>
        <Item to="/campaigns" match={(p) => p.startsWith('/campaigns')}>Campaigns</Item>
        <Item to="/packages">Packages</Item>
      </>
    )
  }

  if (isAdmin) {
    return (
      <>
        <Item to="/admin" match={(p) => p === '/admin'}>
          <span className="inline-flex items-center gap-1"><BarChart3 className="w-4 h-4" /> Traffic</span>
        </Item>
        <Item to="/campaigns" match={(p) => p.startsWith('/campaigns')}>Campaigns</Item>
        <Item to="/audience" match="/audience">Audience</Item>
        <Item to="/packages">Packages</Item>
        <Item to="/inventory">Inventory</Item>
        <Item to="/rfp" match="/rfp">RFP</Item>
      </>
    )
  }

  return null
}

export default Navbar;
